/**
 * Copyright (c) 2022-present TVM Engineering Authors.
 */

import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { createTheme } from "@mui/material/styles";
import { Grid, Typography, ThemeProvider, Link } from "@mui/material";
import SINGUP_BACKGROUND from "../assets/img/landing_main_background.png";
import LoadingScreen from "../components/feedback/LoadingScreen";
import { isLoading } from "../reducers/app";

// interface Props {
// 	isLoading: boolean,
// 	classes: Object,
// }

const PrivacyPolicy = React.memo((/* props: Props */) => {
  const privacyTheme = () =>
    createTheme({
      components: {
        MuiTypography: {
          styleOverrides: {
            root: {
              fontSize: "1.5rem",
              fontWeight: "300",
            },
          },
        },
      },
    });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const {
    // user,
    isPageLoading,
  } = useSelector((globalState) => ({
    // user: getUserData(globalState),
    isPageLoading: isLoading(globalState),
  }));

  if (isPageLoading) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider theme={privacyTheme()}>
      <div style={classes.root}>
        <Grid container sx={classes.container85}>
          <Grid item xs={12}>
            <Typography component="strong" sx={classes.pageTitle}>
              Privacy Policy
            </Typography>
            <Typography component="strong" sx={classes.numTitle}>
              1. An overview of data protection
            </Typography>
            <Typography component="strong" sx={classes.title}>
              General information
            </Typography>
            <Typography component="p">
              The following information will provide you with an easy to navigate overview of what
              will happen with your personal data when you visit this website and use the MotCos
              Portal. The term “personal data” comprises all data that can be used to personally
              identify you. For detailed information about the subject matter of data protection,
              please consult our Data Protection Declaration, which we have included beneath this
              copy.{" "}
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Data recording on this website
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Who is the responsible party for the recording of data on this website (i.e., the
              “controller”)?
            </Typography>
            <Typography component="p">
              The data on this website is processed by the operator of the website, whose contact
              information is available under section “Information about the responsible party
              (referred to as the “controller” in the GDPR)” in this Privacy Policy.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              How do we record your data?
            </Typography>
            <Typography component="p">
              We collect your data as a result of your sharing of your data with us. This may, for
              instance be information you enter into our contact form or registration form. Other
              data shall be recorded by our IT systems automatically or after you consent to its
              recording during your website visit. This data comprises primarily technical
              information (e.g., web browser, operating system, log-in times, activities, periods of
              usage or time the site was accessed). This information is recorded automatically when
              you access this website.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              What are the purposes we use your data for?
            </Typography>
            <Typography component="p">
              A portion of the information is generated to guarantee the error free provision of the
              website. Other data may be used to analyze your user patterns.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              What rights do you have as far as your information is concerned?
            </Typography>
            <Typography component="p">
              You have the right to receive information about the source, recipients, and purposes
              of your archived personal data at any time without having to pay a fee for such
              disclosures. You also have the right to demand that your data are rectified or
              eradicated. If you have consented to data processing, you have the option to revoke
              this consent at any time, which shall affect all future data processing. Moreover, you
              have the right to demand that the processing of your data be restricted under certain
              circumstances. Furthermore, you have the right to log a complaint with the competent
              supervising agency. Please do not hesitate to contact us at any time if you have
              questions about this or any other data protection related issues.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Analysis tools and tools provided by third parties
            </Typography>
            <Typography component="p">
              There is a possibility that your browsing patterns will be statistically analyzed when
              your visit this website. Such analyses are performed primarily with what we refer to
              as analysis programs.
            </Typography>
            <Typography component="strong" sx={classes.numTitle}>
              2. General information and mandatory information
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Data protection
            </Typography>
            <Typography component="p">
              The operators of this website and its pages take the protection of your personal data
              very seriously. Hence, we handle your personal data as confidential information and in
              compliance with the statutory data protection regulations and this Data Protection
              Declaration. Whenever you use this website or the MotCos Portal, a variety of personal
              information will be collected. Personal data comprises data that can be used to
              personally identify you. This Data Protection Declaration explains which data we
              collect as well as the purposes we use this data for. It also explains how, and for
              which purpose the information is collected. We herewith advise you that the
              transmission of data via the internet (i.e., through e-mail communications) may be
              prone to security gaps. It is not possible to completely protect data against
              third-party access.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Information about the responsible party (referred to as the “controller” in the GDPR)
            </Typography>
            <Typography component="p">
              The data processing controller on this website is: Elmos Semiconductor Sales &
              Solutions GmbH, Heinrich-Hertz-Straße 1, 44227 Dortmund, Germany, Phone +49 (0) 231 /
              75 49-0; E-Mail: info@elmos.com <br />
              The controller is the natural person or legal entity that single-handedly or jointly
              with others makes decisions as to the purposes of and resources for the processing of
              personal data (e.g., names, e-mail addresses, etc.).
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Storage duration
            </Typography>
            <Typography component="p">
              Unless a more specific storage period has been specified in this privacy policy, your
              personal data will remain with us until the purpose for which it was collected no
              longer applies. If you assert a justified request for deletion or revoke your consent
              to data processing, your data will be deleted, unless we have other legally
              permissible reasons for storing your personal data (e.g., tax or commercial law
              retention periods); in the latter case, the deletion will take place after these
              reasons cease to apply.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Information on data transfer to the USA and other non-EU countries
            </Typography>
            <Typography component="p">
              Among other things, we use tools of companies domiciled in the United States or other
              from a data protection perspective non-secure non-EU countries. If these tools are
              active, your personal data may potentially be transferred to these non-EU countries
              and may be processed there. We must point out that in these countries, a data
              protection level that is comparable to that in the EU cannot be guaranteed. For
              instance, U.S. enterprises are under a mandate to release personal data to the
              security agencies and you as the data subject do not have any litigation options to
              defend yourself in court. Hence, it cannot be ruled out that U.S. agencies (e.g., the
              Secret Service) may process, analyze, and permanently archive your personal data for
              surveillance purposes. We have no control over these processing activities.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Revocation of your consent to the processing of data
            </Typography>
            <Typography component="p">
              A wide range of data processing transactions are possible only subject to your express
              consent. You can also revoke at any time any consent you have already given us. This
              shall be without prejudice to the lawfulness of any data collection that occurred
              prior to your revocation.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Right to object to the collection of data in special cases; right to object to direct
              advertising (Art. 21 GDPR)
            </Typography>
            <Typography component="p">
              IN THE EVENT THAT DATA ARE PROCESSED ON THE BASIS OF ART. 6(1)(E) OR (F) GDPR, YOU
              HAVE THE RIGHT TO AT ANY TIME OBJECT TO THE PROCESSING OF YOUR PERSONAL DATA BASED ON
              GROUNDS ARISING FROM YOUR UNIQUE SITUATION. THIS ALSO APPLIES TO ANY PROFILING BASED
              ON THESE PROVISIONS. TO DETERMINE THE LEGAL BASIS, ON WHICH ANY PROCESSING OF DATA IS
              BASED, PLEASE CONSULT THIS DATA PROTECTION DECLARATION. IF YOU LOG AN OBJECTION, WE
              WILL NO LONGER PROCESS YOUR AFFECTED PERSONAL DATA, UNLESS WE ARE IN A POSITION TO
              PRESENT COMPELLING PROTECTION WORTHY GROUNDS FOR THE PROCESSING OF YOUR DATA, THAT
              OUTWEIGH YOUR INTERESTS, RIGHTS AND FREEDOMS OR IF THE PURPOSE OF THE PROCESSING IS
              THE CLAIMING, EXERCISING OR DEFENCE OF LEGAL ENTITLEMENTS (OBJECTION PURSUANT TO ART.
              21(1) GDPR). IF YOUR PERSONAL DATA IS BEING PROCESSED IN ORDER TO ENGAGE IN DIRECT
              ADVERTISING, YOU HAVE THE RIGHT TO OBJECT TO THE PROCESSING OF YOUR AFFECTED PERSONAL
              DATA FOR THE PURPOSES OF SUCH ADVERTISING AT ANY TIME. THIS ALSO APPLIES TO PROFILING
              TO THE EXTENT THAT IT IS AFFILIATED WITH SUCH DIRECT ADVERTISING. IF YOU OBJECT, YOUR
              PERSONAL DATA WILL SUBSEQUENTLY NO LONGER BE USED FOR DIRECT ADVERTISING PURPOSES
              (OBJECTION PURSUANT TO ART. 21(2) GDPR). Right to log a complaint with the competent
              supervisory agency In the event of violations of the GDPR, data subjects are entitled
              to log a complaint with a supervisory agency, in particular in the member state where
              they usually maintain their domicile, place of work or at the place where the alleged
              violation occurred. The right to log a complaint is in effect regardless of any other
              administrative or court proceedings available as legal recourses.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Right to data portability
            </Typography>
            <Typography component="p">
              You have the right to demand that we hand over any data we automatically process on
              the basis of your consent or in order to fulfil a contract be handed over to you or a
              third party in a commonly used, machine readable format. If you should demand the
              direct transfer of the data to another controller, this will be done only if it is
              technically feasible.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              SSL and/or TLS encryption
            </Typography>
            <Typography component="p">
              For security reasons and to protect the transmission of confidential content, such as
              purchase orders or inquiries you submit to us as the website operator, this website
              uses either an SSL or a TLS encryption program. You can recognize an encrypted
              connection by checking whether the address line of the browser switches from “http://”
              to “https://” and also by the appearance of the lock icon in the browser line. If the
              SSL or TLS encryption is activated, data you transmit to us cannot be read by third
              parties.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Information about, rectification and eradication of data
            </Typography>
            <Typography component="p">
              Within the scope of the applicable statutory provisions, you have the right to at any
              time demand information about your archived personal data, their source and recipients
              as well as the purpose of the processing of your data. You may also have a right to
              have your data rectified or eradicated. If you have questions about this subject
              matter or any other questions about personal data, please do not hesitate to contact
              us at any time.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Right to demand processing restrictions
            </Typography>
            <Typography component="p">
              You have the right to demand the imposition of restrictions as far as the processing
              of your personal data is concerned. To do so, you may contact us at any time. The
              right to demand restriction of processing applies in the following cases:
              <Typography component="li" sx={classes.bullets}>
                In the event that you should dispute the correctness of your data archived by us, we
                will usually need some time to verify this claim. During the time that this
                investigation is ongoing, you have the right to demand that we restrict the
                processing of your personal data.
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                If the processing of your personal data was/is conducted in an unlawful manner, you
                have the option to demand the restriction of the processing of your data in lieu of
                demanding the eradication of this data.
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                If we do not need your personal data any longer and you need it to exercise, defend
                or claim legal entitlements, you have the right to demand the restriction of the
                processing of your personal data instead of its eradication.
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                If you have raised an objection pursuant to Art. 21(1) GDPR, your rights and our
                rights will have to be weighed against each other. As long as it has not been
                determined whose interests prevail, you have the right to demand a restriction of
                the processing of your personal data.
              </Typography>
              If you have restricted the processing of your personal data, these data – with the
              exception of their archiving – may be processed only subject to your consent or to
              claim, exercise or defend legal entitlements or to protect the rights of other natural
              persons or legal entities or for important public interest reasons cited by the
              European Union or a member state of the EU.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Rejection of unsolicited e-mails
            </Typography>
            <Typography component="p">
              We herewith object to the use of contact information published in conjunction with the
              mandatory information to be provided in our Site Notice to send us promotional and
              information material that we have not expressly requested. The operators of this
              website and its pages reserve the express right to take legal action in the event of
              the unsolicited sending of promotional information, for instance via SPAM messages.
            </Typography>
            <Typography component="strong" sx={classes.numTitle}>
              3. Recording of data on this website
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Cookies
            </Typography>
            <Typography component="p">
              Our websites and pages use what the industry refers to as “cookies.” Cookies are small
              text files that do not cause any damage to your device. They are either stored
              temporarily for the duration of a session (session cookies) or they are permanently
              archived on your device (permanent cookies). Session cookies are automatically deleted
              once you terminate your visit. Permanent cookies remain archived on your device until
              you actively delete them, or they are automatically eradicated by your web browser. In
              some cases, it is possible that third-party cookies are stored on your device once you
              enter our site (third-party cookies). These cookies enable you or us to take advantage
              of certain services offered by the third party (e.g., cookies for the processing of
              payment services). Cookies have a variety of functions. Many cookies are technically
              essential since certain website functions would not work in the absence of the cookies
              (e.g., the shopping cart function or the display of videos). The purpose of other
              cookies may be the analysis of user patterns or the display of promotional messages.
              Cookies, which are required for the performance of electronic communication
              transactions (required cookies) or for the provision of certain functions you want to
              use (functional cookies, e.g., for the shopping cart function) or those that are
              necessary for the optimization of the website (e.g., cookies that provide measurable
              insights into the web audience), shall be stored on the basis of Art. 6(1)(f) GDPR,
              unless a different legal basis is cited. The operator of the website has a legitimate
              interest in the storage of cookies to ensure the technically error free and optimized
              provision of the operator’s services. If your consent to the storage of the cookies
              has been requested, the respective cookies are stored exclusively on the basis of the
              consent obtained (Art. 6(1)(a) GDPR); this consent may be revoked at any time. You
              have the option to set up your browser in such a manner that you will be notified any
              time cookies are placed and to permit the acceptance of cookies only in specific
              cases. You may also exclude the acceptance of cookies in certain cases or in general
              or activate the delete function for the automatic eradication of cookies when the
              browser closes. If cookies are deactivated, the functions of this website may be
              limited. In the event that third-party cookies are used or if cookies are used for
              analytical purposes, we will separately notify you in conjunction with this Data
              Protection Policy and, if applicable, ask for your consent.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Server log files
            </Typography>
            <Typography component="p">
              The provider of this website and its pages automatically collects and stores
              information in so-called server log files, which your browser communicates to us
              automatically. The information comprises:
              <Typography component="li" sx={classes.bullets}>
                The type and version of browser used
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                The used operating system
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                Referrer URL
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                The hostname of the accessing computer
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                The time of the server inquiry
              </Typography>
              <Typography component="li" sx={classes.bullets}>
                The IP address
              </Typography>
              This data is not merged with other data sources. This data is recorded on the basis of
              Art. 6(1)(f) GDPR. The operator of the website has a legitimate interest in the
              technically error free depiction and the optimization of the operator’s website. In
              order to achieve this, server log files must be recorded.
            </Typography>
            <Typography component="p">
              Contact form or registration form If you submit inquiries to us via our contact form
              or registration form, the information provided in the contact form or registration
              form as well as any contact information provided therein will be stored by us in order
              to handle your inquiry and in the event that we have further questions. We will not
              share this information without your consent. The processing of these data is based on
              Art. 6(1)(b) GDPR, if your request is related to the execution of a contract or if it
              is necessary to carry out pre-contractual measures. In all other cases the processing
              is based on our legitimate interest in the effective processing of the requests
              addressed to us (Art. 6(1)(f) GDPR) or on your agreement (Art. 6(1)(a) GDPR) if this
              has been requested. The information you have entered into the contact form or
              registration form shall remain with us until you ask us to eradicate the data, revoke
              your consent to the archiving of data or if the purpose for which the information is
              being archived no longer exists (e.g., after we have concluded our response to your
              inquiry). This shall be without prejudice to any mandatory legal provisions, in
              particular retention periods.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Use of Account
            </Typography>
            <Typography component="p">
              When using the MotCos Portal through an User account, the MotCos Portal will track the
              login times, activities, period of usage. The processing of these data is based on
              Art. 6(1)(b) GDPR, if your request is related to the execution of a contract or if it
              is necessary to carry out pre-contractual measures. In all other cases the processing
              is based on our legitimate interest in the effective processing of the requests
              addressed to us (Art. 6(1)(f) GDPR) or on your agreement (Art. 6(1)(a) GDPR) if this
              has been requested. The information we have gathered by tracking the user account
              shall remain with us until you ask us to eradicate the data, revoke your consent to
              the archiving of data or if the purpose for which the information is being archived no
              longer exists (e.g., after we have concluded our response to your inquiry). This shall
              be without prejudice to any mandatory legal provisions, in particular retention
              periods.
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Hubspot CRM
            </Typography>
            <Typography component="p">
              We use Hubspot CRM on this website. The provider is Hubspot Inc. 25 Street, Cambridge,
              MA 02141 USA (hereafter Hubspot CRM). Hubspot CRM enables us, among other things, to
              manage existing and potential customers and customer contacts, to communicate with you
              and to plan and execute marketing activities in line with your interests. Hubspot CRM
              enables us to capture, sort and analyze customer interactions via email, social media,
              or phone across multiple channels. The personal data collected in this way can be
              evaluated and used for communication with the potential customer or marketing measures
              (e.g., newsletter mailings). Hubspot CRM also enables us to collect and analyze the
              user behavior of our contacts on our website. The use of Hubspot CRM is based on Art.
              6(1)(f) GDPR. The website operator has a legitimate interest in the most efficient
              customer management and customer communication. If consent has been requested, the
              processing is carried out exclusively on the basis of Art. 6(1)(a) GDPR; consent may
              be revoked at any time. For details, please refer to Hubspot’s privacy policy:{" "}
              <Link href="https://legal.hubspot.com/de/privacy-policy" target="blank">
                https://legal.hubspot.com/de/privacy-policy
              </Link>
              . Data transmission to the US is based on the standard contractual clauses of the EU
              Commission. Details can be found here:{" "}
              <Link href="https://www.hubspot.de/data-privacy/privacy-shield" target="blank">
                https://www.hubspot.de/data-privacy/privacy-shield
              </Link>
              .
            </Typography>
            <Typography component="strong" sx={classes.numTitle}>
              4. Analysis tools and advertising
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Google Analytics
            </Typography>
            <Typography component="p">
              This website uses functions of the web analysis service Google Analytics. The provider
              of this service is Google Ireland Limited (“Google”), Gordon House, Barrow Street,
              Dublin 4, Ireland. Google Analytics enables the website operator to analyze the
              behavior patterns of website visitors. To that end, the website operator receives a
              variety of user data, such as pages accessed, time spent on the page, the utilized
              operating system and the user’s origin. Google may consolidate these data in a profile
              that is allocated to the respective user or the user’s device. Google Analytics uses
              technologies that make the recognition of the user for the purpose of analyzing the
              user behavior patterns (e.g., cookies or device fingerprinting). The website use
              information recorded by Google is, as a rule transferred to a Google server in the
              United States, where it is stored. This analysis tool is used on the basis of Art.
              6(1)(f) GDPR. The operator of this website has a legitimate interest in the analysis
              of user patterns to optimize both, the services offered online and the operator’s
              advertising activities. If a corresponding agreement has been requested (e.g., an
              agreement to the storage of cookies), the processing takes place exclusively on the
              basis of Art. 6(1)(a) GDPR; the agreement can be revoked at any time. Data
              transmission to the US is based on the Standard Contractual Clauses (SCC) of the
              European Commission. Details can be found here:{" "}
              <Link
                href="https://privacy.google.com/businesses/controllerterms/mccs/"
                target="blank"
              >
                https://privacy.google.com/businesses/controllerterms/mccs/
              </Link>
              .
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Browser plug-in
            </Typography>
            <Typography component="p">
              You can prevent the recording and processing of your data by Google by downloading and
              installing the browser plugin available under the following link:{" "}
              <Link href="https://tools.google.com/dlpage/gaoptout?hl=en" target="blank">
                https://tools.google.com/dlpage/gaoptout?hl=en
              </Link>
              . For more information about the handling of user data by Google Analytics, please
              consult Google’s Data Privacy Declaration at:{" "}
              <Link href="https://support.google.com/analytics/answer/6004245?hl=en" target="blank">
                https://support.google.com/analytics/answer/6004245?hl=en
              </Link>
              . Contract data processing We have executed a contract data processing agreement with
              Google and are implementing the stringent provisions of the German data protection
              agencies to the fullest when using Google Analytics.
            </Typography>
            <Typography component="strong" sx={classes.numTitle}>
              5. Plug-ins and Tools
            </Typography>
            <Typography component="strong" sx={classes.title}>
              Google Web Fonts
            </Typography>
            <Typography component="p">
              To ensure that fonts used on this website are uniform, this website uses so-called Web
              Fonts provided by Google. When you access a page on our website, your browser will
              load the required web fonts into your browser cache to correctly display text and
              fonts. To do this, the browser you use will have to establish a connection with
              Google’s servers. As a result, Google will learn that your IP address was used to
              access this website. The use of Google Web Fonts is based on Art. 6(1)(f) GDPR. The
              website operator has a legitimate interest in a uniform presentation of the font on
              the operator’s website. If a respective declaration of consent has been obtained
              (e.g., consent to the archiving of cookies), the data will be processed exclusively on
              the basis of Art. 6(1)(a) GDPR. Any such consent may be revoked at any time. If your
              browser should not support Web Fonts, a standard font installed on your computer will
              be used. For more information on Google Web Fonts, please follow this link:{" "}
              <Link href="https://developers.google.com/fonts/faq" target="blank">
                https://developers.google.com/fonts/faq
              </Link>{" "}
              and consult Google’s Data Privacy Declaration under:{" "}
              <Link href="https://policies.google.com/privacy?hl=en" target="blank">
                https://policies.google.com/privacy?hl=en
              </Link>
              .
            </Typography>
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  );
});

export default PrivacyPolicy;

const classes = {
  root: {
    minHeight: "750px",
    background: "#DDE5EF",
    overflowX: "hidden",
    overflowY: "hidden",
    padding: 20,
    backgroundImage: `url(${SINGUP_BACKGROUND})`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    fontWeight: "300",
  },
  container85: {
    width: "85%",
    margin: "0 auto",
    minHeight: "750px",
    background: "#fff",
    padding: "15px 30px",
  },
  pageTitle: {
    color: "#1f577b",
    fontSize: "1.7rem",
    fontWeight: "500",
    marginBottom: "15px",
    marginTop: "50px",
  },
  numTitle: {
    fontWeight: "500",
    display: "block",
    marginBottom: "10px",
    fontSize: "1.5rem",
    marginTop: "20px",
  },
  title: {
    fontWeight: "500",
    display: "block",
    marginBottom: "10px",
    marginTop: "20px",
  },
  bullets: {
    padding: "10px 0px",
  },
};
